import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import type { PropertyEditorProps } from '@/lib/action-modules/registry/subtypes/types'

function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every(v => typeof v === 'number')
}

export function CelebratedAnniversariesEditor(props: PropertyEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const value = props.value
  if (value !== undefined && value !== null && !isNumberArray(value)) return <>{t('editor.invalidValue')}</>
  return <Editor value={value ?? []} onChanged={v => props.onChanged(v)} />
}

function Editor({ value, onChanged }: { value: number[]; onChanged: (v: number[]) => void }) {
  const { t } = useTranslation('action-modules-wizard')
  const onDelete = useCallback((index: number) => onChanged(value.filter((_, i) => i !== index)), [value, onChanged])

  const onAdd = useCallback(() => onChanged([...value, 0]), [value, onChanged])

  const onItemChanged = useCallback(
    (index: number, next: number) => onChanged(value.map((v, i) => (i === index ? next : v))),
    [value, onChanged],
  )

  return (
    <div className="flex flex-col items-stretch gap-1">
      {value.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <Input
            type="number"
            value={item}
            onChange={e => onItemChanged(index, e.target.value !== '' ? Number(e.target.value) : 0)}
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => onDelete(index)}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={onAdd}>
        <Plus className="size-4" /> {t('buttons.add')}
      </Button>
    </div>
  )
}
